import { useEffect, useRef } from "react";
import { useInstructorVoiceContext } from "@/providers/InstructorVoiceProvider";
import { getMoment, INSTRUCTOR_LABELS } from "@/lib/audio/instructorVoiceMap";

/**
 * Instructor voice hook.
 *
 * - `playMoment(momentId)`: fires the configured beta voice line once
 *   per session. Safe to call from event handlers or effects — the
 *   underlying controller dedupes, debounces against music, and
 *   ducks background audio.
 * - `playMomentOnce(momentId, condition)`: convenience for `useEffect`
 *   triggers — fires the moment the first time `condition` becomes
 *   true, and never again from this component.
 */
export function useInstructorVoice() {
  const ctx = useInstructorVoiceContext();
  return {
    enabled: ctx.enabled,
    setEnabled: ctx.setEnabled,
    volume: ctx.volume,
    setVolume: ctx.setVolume,
    speakingMomentId: ctx.speakingMomentId,
    playMoment: ctx.playMoment,
    preload: ctx.preload,
    stop: ctx.stop,
    getMoment,
    getInstructorLabel: (id: string) =>
      INSTRUCTOR_LABELS[id as keyof typeof INSTRUCTOR_LABELS] ?? id,
  };
}

/**
 * Convenience hook: triggers a moment when `condition` first becomes
 * truthy. Stable across rerenders/scroll — only fires once per mount.
 */
export function useTriggerInstructorVoice(momentId: string, condition: boolean) {
  const { playMoment } = useInstructorVoice();
  const firedRef = useRef(false);
  const pendingRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (pendingRef.current) return;
    if (!condition) return;
    pendingRef.current = true;
    void playMoment(momentId).then((result) => {
      if (["played", "suppressed", "already_played"].includes(result.status)) {
        firedRef.current = true;
      }
    }).finally(() => {
      pendingRef.current = false;
    });
  }, [condition, momentId, playMoment]);
}

/**
 * Preload cached signed URLs for high-priority moments on mount so the
 * first playback is instant. For approved static moments, preload also
 * initiates generation when missing without marking moments as fired.
 */
export function usePreloadInstructorVoice(momentIds: string[]) {
  const { preload } = useInstructorVoice();
  const keyRef = useRef<string>("");
  useEffect(() => {
    const key = momentIds.join("|");
    if (key === keyRef.current) return;
    keyRef.current = key;
    preload(momentIds);
  }, [momentIds, preload]);
}